import { useState, useContext, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ToastContainer, toast } from "react-toastify"
import userContext from "../context/userContext"
import ImageSelect from "./ImageSelect"
import PasswordChecklist from "./PasswordChecklist"
import { passwordOk } from "../util/passwordRules"

const PHONE = /^\+?[\d\s()-]{7,20}$/
const EMAIL = /^\S+@\S+\.\S+$/

// Address parts live under business.address; everything else is top-level
const ADDRESS_KEYS = ["line1", "city", "province", "zip"]
const AUTOCOMPLETE = {
  name: "organization",
  phone: "tel",
  line1: "address-line1",
  city: "address-level2",
  province: "address-level1",
  zip: "postal-code",
}

const emptyBusiness = (email = "") => ({
  name: "",
  email,
  phone: "",
  address: { line1: "", city: "", province: "", zip: "" },
  type: "",
  subtype: "",
})

const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)

/**
 * Sign-up page. Its text, business fields and extra questions come from
 * Admin → Sign Up. With `previewConfig` it renders that config instead and
 * never submits (used for the live preview in the admin editor).
 */
const SignUp = ({ previewConfig }) => {
  const preview = !!previewConfig
  const { setUser } = useContext(userContext)
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [loadedConfig, setLoadedConfig] = useState(null)
  const cfg = previewConfig || loadedConfig

  const [account, setAccount] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordConfirm: "",
  })
  const [businesses, setBusinesses] = useState([emptyBusiness()])
  const [answers, setAnswers] = useState({})

  /* Business types come from the store's categories */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/categories`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setCategories(Array.isArray(list) ? list : []))
      .catch(() => setCategories([]))
  }, [])

  /* The page's settings (skipped in the admin preview) */
  useEffect(() => {
    if (preview) return
    fetch(`${process.env.REACT_APP_API_URL}/signup/form`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => setLoadedConfig(data.config))
      .catch(() => toast.error("Sign-up is unavailable right now. Please try again.", { position: "bottom-left" }))
  }, [preview])

  if (!cfg) return null

  const fields = cfg.businessFields
  const questions = cfg.questions || []
  const totalSteps = questions.length ? 3 : 2
  const stepTitle = [cfg.step1Title, cfg.step2Title, cfg.step3Title][step - 1]

  const subtypesFor = (type) =>
    categories.find((c) => c.name === type)?.subCategories || []
  const subtypeImage = (type, sub) =>
    categories.find((c) => c.name === type)?.subCategoryImages?.[sub]
  const subtypeLabel = (type) => fields.subtype.label.replace("{type}", type)

  /* =====================================================
     HANDLERS
  ===================================================== */
  const handleError = (msg) => toast.error(msg, { position: "bottom-left" })
  const handleSuccess = (msg) => toast.success(msg, { position: "bottom-right" })

  const onAccountChange = (e) =>
    setAccount((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  const updateBusiness = (index, patch) =>
    setBusinesses((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)))

  const updateAddress = (index, field, value) =>
    setBusinesses((prev) =>
      prev.map((b, i) =>
        i === index ? { ...b, address: { ...b.address, [field]: value } } : b,
      ),
    )

  const setBizValue = (i, key, value) =>
    ADDRESS_KEYS.includes(key) ? updateAddress(i, key, value) : updateBusiness(i, { [key]: value })
  const bizValue = (b, key) => (ADDRESS_KEYS.includes(key) ? b.address[key] : b[key])

  const addBusiness = () => setBusinesses((prev) => [...prev, emptyBusiness()])
  const removeBusiness = (index) =>
    setBusinesses((prev) => prev.filter((_, i) => i !== index))

  const setAnswer = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }))

  /* =====================================================
     VALIDATION (the server checks the same rules)
  ===================================================== */
  const validateAccount = () => {
    const { firstName, lastName, email, password, passwordConfirm } = account
    if (!firstName.trim() || !lastName.trim()) return "First and last name are required"
    if (!EMAIL.test(email.trim())) return "Enter a valid email address"
    if (!passwordOk(password)) return "Your password doesn't meet all the requirements yet"
    if (password !== passwordConfirm) return "Passwords do not match"
    return null
  }

  const validateBusinesses = () => {
    for (const [i, b] of businesses.entries()) {
      const n = businesses.length > 1 ? ` (business ${i + 1})` : ""
      for (const key of ["name", "email", "phone", ...ADDRESS_KEYS]) {
        const f = fields[key]
        if (f.show && f.required && !bizValue(b, key).trim()) return `${f.label} is required${n}`
      }
      if (fields.email.show && b.email.trim() && !EMAIL.test(b.email.trim()))
        return `Enter a valid ${fields.email.label.toLowerCase()}${n}`
      if (fields.phone.show && b.phone.trim() && !PHONE.test(b.phone.trim()))
        return `Enter a valid ${fields.phone.label.toLowerCase()}${n}`
      if (!b.type) return `Choose a ${fields.type.label.toLowerCase()}${n}`
      if (fields.subtype.show && fields.subtype.required && subtypesFor(b.type).length && !b.subtype)
        return `${subtypeLabel(b.type)} is required${n}`
    }
    return null
  }

  const validateAnswers = () => {
    for (const q of questions) {
      const v = typeof answers[q.id] === "string" ? answers[q.id].trim() : answers[q.id]
      if (q.required && isEmpty(v)) return `"${q.label}" is required`
      if (q.type === "number" && !isEmpty(v) && !Number.isFinite(Number(v)))
        return `"${q.label}" must be a number`
    }
    return null
  }

  // The preview lets the admin click through without filling anything in
  const check = (validate) => {
    if (preview) return true
    const problem = validate()
    if (problem) handleError(problem)
    return !problem
  }

  const handleNext = () => {
    if (step === 1) {
      if (!check(validateAccount)) return
      // Most owners use the same email for their first business; they can change it
      if (fields.email.show && !businesses[0].email)
        updateBusiness(0, { email: account.email.trim() })
      return setStep(2)
    }
    if (step === 2 && check(validateBusinesses)) setStep(3)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (step < totalSteps) return handleNext()

    if (preview) return handleSuccess("Preview only — nothing was saved")
    if (!check(validateBusinesses) || !check(validateAnswers)) return

    const payload = {
      firstName: account.firstName.trim(),
      lastName: account.lastName.trim(),
      email: account.email.trim(),
      password: account.password,
      businesses: cfg.allowMultipleBusinesses ? businesses : businesses.slice(0, 1),
      answers,
    }

    try {
      setLoading(true)

      const res = await fetch(`${process.env.REACT_APP_API_URL}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      })

      const data = await res.json()

      if (!data.success) return handleError(data.message)

      handleSuccess(data.message)
      setUser(data.user)

      // The account page sends unconfirmed accounts to "Confirm your email"
      setTimeout(() => navigate("/account"), 1000)
    } catch {
      handleError("Signup failed")
    } finally {
      setLoading(false)
    }
  }

  /* =====================================================
     UI
  ===================================================== */
  const field = (label, props) => (
    <div className="form-floating mb-3">
      <input className="form-control" placeholder={label} {...props} />
      <label htmlFor={props.id}>{label}</label>
    </div>
  )

  const optional = (f) => (f.required ? f.label : `${f.label} (optional)`)

  const businessInput = (b, i, key, colClass) => {
    const f = fields[key]
    if (!f.show) return null
    const input = field(optional(f), {
      id: `biz${i}-${key}`,
      type: key === "email" ? "email" : key === "phone" ? "tel" : "text",
      autoComplete: AUTOCOMPLETE[key],
      value: bizValue(b, key),
      onChange: (e) => setBizValue(i, key, e.target.value),
    })
    return colClass ? <div className={colClass}>{input}</div> : input
  }

  const questionInput = (q) => {
    const id = `q-${q.id}`
    const label = q.required ? q.label : `${q.label} (optional)`
    const value = answers[q.id]
    const help = q.help && <div className="form-text">{q.help}</div>

    if (q.type === "select")
      return (
        <div className="mb-3" key={q.id}>
          <label htmlFor={id} className="form-label fw-semibold">{label}</label>
          <select id={id} className="form-select" value={value || ""} onChange={(e) => setAnswer(q.id, e.target.value)}>
            <option value="">Choose…</option>
            {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {help}
        </div>
      )

    if (q.type === "checkboxes" || q.type === "yesno") {
      const opts = q.type === "yesno" ? [["yes", "Yes"], ["no", "No"]] : q.options.map((o) => [o, o])
      const multi = q.type === "checkboxes"
      const picked = multi ? value || [] : value
      return (
        <fieldset className="mb-3" key={q.id}>
          <legend className="form-label fw-semibold fs-6">{label}</legend>
          {opts.map(([v, text]) => (
            <div className={`form-check ${multi ? "" : "form-check-inline"}`} key={v}>
              <input
                id={`${id}-${v}`}
                className="form-check-input"
                type={multi ? "checkbox" : "radio"}
                name={id}
                checked={multi ? picked.includes(v) : picked === v}
                onChange={() =>
                  setAnswer(q.id, multi ? (picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]) : v)
                }
              />
              <label className="form-check-label" htmlFor={`${id}-${v}`}>{text}</label>
            </div>
          ))}
          {help}
        </fieldset>
      )
    }

    if (q.type === "textarea")
      return (
        <div className="mb-3" key={q.id}>
          <label htmlFor={id} className="form-label fw-semibold">{label}</label>
          <textarea id={id} className="form-control" rows={3} value={value || ""} onChange={(e) => setAnswer(q.id, e.target.value)} />
          {help}
        </div>
      )

    return (
      <div className="mb-3" key={q.id}>
        <label htmlFor={id} className="form-label fw-semibold">{label}</label>
        <input
          id={id}
          className="form-control"
          type={q.type === "number" ? "number" : "text"}
          value={value ?? ""}
          onChange={(e) => setAnswer(q.id, e.target.value)}
        />
        {help}
      </div>
    )
  }

  const form = (
    <form
      className="p-4 p-md-5 border rounded-3 bg-light text-start"
      onSubmit={handleSubmit}
      noValidate
    >
      <h1 className="display-4 mb-1 text-center">{cfg.title}</h1>
      <p className="text-muted text-center mb-4">
        Step {step} of {totalSteps} — {stepTitle}
      </p>

      {/* ================= STEP 1: YOUR DETAILS ================= */}
      {step === 1 && (
        <>
          <div className="row g-2">
            <div className="col-sm-6">
              {field("First name", {
                id: "firstName",
                name: "firstName",
                autoComplete: "given-name",
                value: account.firstName,
                onChange: onAccountChange,
              })}
            </div>
            <div className="col-sm-6">
              {field("Last name", {
                id: "lastName",
                name: "lastName",
                autoComplete: "family-name",
                value: account.lastName,
                onChange: onAccountChange,
              })}
            </div>
          </div>

          {field("Email address (for logging in)", {
            id: "email",
            name: "email",
            type: "email",
            autoComplete: "email",
            value: account.email,
            onChange: onAccountChange,
          })}
          <div className="form-floating mb-2">
            <input
              className="form-control"
              placeholder="Password"
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              aria-describedby="password-rules"
              value={account.password}
              onChange={onAccountChange}
            />
            <label htmlFor="password">Password</label>
          </div>
          <PasswordChecklist id="password-rules" password={account.password} />
          {field("Confirm password", {
            id: "passwordConfirm",
            name: "passwordConfirm",
            type: "password",
            autoComplete: "new-password",
            value: account.passwordConfirm,
            onChange: onAccountChange,
          })}
        </>
      )}

      {/* ================= STEP 2: YOUR BUSINESSES ================= */}
      {step === 2 && (
        <>
          {businesses.map((b, i) => {
            const subtypes = fields.subtype.show ? subtypesFor(b.type) : []
            const id = (f) => `biz${i}-${f}`
            return (
              <fieldset key={i} className="border rounded-3 p-3 mb-3 bg-white">
                <div className="d-flex align-items-center mb-2">
                  <legend className="fs-5 fw-semibold mb-0 me-auto float-none w-auto">
                    Business {businesses.length > 1 ? i + 1 : ""}
                  </legend>
                  {businesses.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeBusiness(i)}
                      aria-label={`Remove business ${i + 1}`}
                    >
                      Remove
                    </button>
                  )}
                </div>

                {businessInput(b, i, "name")}

                <div className="row g-2">
                  {businessInput(b, i, "email", "col-sm")}
                  {businessInput(b, i, "phone", "col-sm")}
                </div>

                {businessInput(b, i, "line1")}
                <div className="row g-2">
                  {businessInput(b, i, "city", "col-sm-5")}
                  {businessInput(b, i, "province", "col-sm-4")}
                  {businessInput(b, i, "zip", "col-sm-3")}
                </div>

                <div className="row g-2">
                  <div className={subtypes.length ? "col-sm-6" : "col-12"}>
                    <ImageSelect
                      id={id("type")}
                      label={fields.type.label}
                      value={b.type}
                      onChange={(v) => updateBusiness(i, { type: v, subtype: "" })}
                      options={categories.map((c) => ({ value: c.name, label: c.name, image: c.image }))}
                    />
                  </div>

                  {subtypes.length > 0 && (
                    <div className="col-sm-6">
                      <ImageSelect
                        id={id("subtype")}
                        label={fields.subtype.required ? subtypeLabel(b.type) : `${subtypeLabel(b.type)} (optional)`}
                        value={b.subtype}
                        onChange={(v) => updateBusiness(i, { subtype: v })}
                        options={[
                          ...(fields.subtype.required ? [] : [{ value: "", label: "Not sure / other" }]),
                          ...subtypes.map((sub) => ({ value: sub, label: sub, image: subtypeImage(b.type, sub) })),
                        ]}
                      />
                    </div>
                  )}
                </div>
              </fieldset>
            )
          })}

          {cfg.allowMultipleBusinesses && (
            <button
              type="button"
              className="w-100 btn btn-outline-success mb-4"
              onClick={addBusiness}
            >
              <span aria-hidden="true">＋</span> Add another business
            </button>
          )}
        </>
      )}

      {/* ================= STEP 3: EXTRA QUESTIONS ================= */}
      {step === 3 && questions.map(questionInput)}

      {/* ================= BUTTONS ================= */}
      {step < totalSteps ? (
        <button type="button" className="w-100 btn btn-lg btn-primary mb-3" onClick={handleNext}>
          Continue
        </button>
      ) : (
        <button disabled={loading} className="w-100 btn btn-lg btn-primary mb-3" type="submit">
          {loading ? "Creating..." : cfg.submitLabel}
        </button>
      )}

      {step > 1 && (
        <button
          type="button"
          className="w-100 btn btn-outline-secondary mb-3"
          onClick={() => setStep(step - 1)}
        >
          Back
        </button>
      )}

      <small className="text-muted">
        Already have an account? <Link to="/login">Login</Link>
      </small>

      <hr className="mt-4" />
      <small className="text-muted my-4">{cfg.termsText}</small>
    </form>
  )

  if (preview)
    return (
      <>
        {form}
        <ToastContainer />
      </>
    )

  return (
    <div className="SignUp justify-content-center d-flex h-100 align-items-center">
      <div className="row col-xl-7 col-md-9 col-11 pt-5 pb-4 mx-2">
        {form}
        <ToastContainer />
      </div>
    </div>
  )
}

export default SignUp
